import { RiskAssessmentStatus, type RiskAssessmentDecision, type Step2Response } from "types";

interface UseNewStep2EvaluationStateParams {
  approvalFormIsApproved: boolean | null;
  approvals: NonNullable<Step2Response["approvals"]>;
  initialValues?: Step2Response | null;
  isCurrentVersion: boolean;
  loggedUserId?: number | string | null;
  readOnly: boolean;
}

export const useNewStep2EvaluationState = ({
  approvalFormIsApproved,
  approvals,
  initialValues,
  isCurrentVersion,
  loggedUserId,
  readOnly,
}: UseNewStep2EvaluationStateParams) => {
  const isApprovalRejected = approvalFormIsApproved === false;

  const step1ApprovalRejected = approvals.length > 0 && approvals[0].approved === false;

  const needsStep1Approval = !approvals.length;

  const processSafetyStatus = initialValues?.risk_assessment_status ?? null;

  const isProcessSafetyReviewPending = processSafetyStatus === RiskAssessmentStatus.WAITING_REVIEW;

  const isProcessSafetyComplementPending =
    processSafetyStatus === RiskAssessmentStatus.COMPLEMENT_PENDING;

  const isProcessSafetyReviewRejected =
    processSafetyStatus === RiskAssessmentStatus.MORE_INFORMATION_REQUIRED;

  const isProcessSafetyCompleted = processSafetyStatus === RiskAssessmentStatus.COMPLETED;

  const riskAssessmentDecisions = initialValues?.risk_assessment_decisions ?? [];

  const riskAssessmentUserId = initialValues?.risk_assessment_submitted_by?.user_id;

  const riskAssessmentWasSubmittedByLoggedUser =
    loggedUserId != null &&
    riskAssessmentUserId != null &&
    String(loggedUserId) === String(riskAssessmentUserId);

  const canAnswerProcessSafetyReview =
    !readOnly && isCurrentVersion && !riskAssessmentWasSubmittedByLoggedUser;

  const shouldShowProcessSafetyReview =
    !step1ApprovalRejected &&
    !needsStep1Approval &&
    isProcessSafetyReviewPending &&
    canAnswerProcessSafetyReview;

  const shouldShowProcessSafetyComplement =
    !step1ApprovalRejected &&
    !needsStep1Approval &&
    (isProcessSafetyComplementPending || isProcessSafetyCompleted);

  const shouldShowStep2Assessment = !step1ApprovalRejected && !needsStep1Approval;

  const step2AssessmentReadOnly =
    readOnly ||
    isProcessSafetyReviewPending ||
    isProcessSafetyComplementPending ||
    isProcessSafetyCompleted;

  const processSafetyComplementReadOnly =
    readOnly ||
    !isCurrentVersion ||
    isProcessSafetyCompleted ||
    riskAssessmentWasSubmittedByLoggedUser;

  const approvalButtonLabelKey = isApprovalRejected ? "ctoStep2.returnToStep1" : "generic.approve";

  const processSafetyApprovalButtonLabelKey = isApprovalRejected
    ? "ctoStep2.requestRevision"
    : "generic.approve";

  return {
    approvalButtonLabelKey,
    canAnswerProcessSafetyReview,
    isApprovalRejected,
    isProcessSafetyComplementPending,
    isProcessSafetyCompleted,
    isProcessSafetyReviewPending,
    isProcessSafetyReviewRejected,
    needsStep1Approval,
    processSafetyApprovalButtonLabelKey,
    riskAssessmentDecisions: riskAssessmentDecisions as RiskAssessmentDecision[],
    riskAssessmentWasSubmittedByLoggedUser,
    processSafetyComplementReadOnly,
    processSafetyStatus,
    shouldShowProcessSafetyComplement,
    shouldShowProcessSafetyReview,
    shouldShowStep2Assessment,
    step1ApprovalRejected,
    step2AssessmentReadOnly,
  };
};
